/**
 * Posting-age helper — single source of truth for how old a job ad is and
 * whether it should be flagged as "may be stale".
 *
 * The 14-day threshold is anchored to the one board behaviour we have actually
 * documented: a free LinkedIn post auto-pauses at day 14 (and auto-closes at
 * day 30 if not promoted; promoted posts can run up to 6 months). No equivalent
 * figure has been established for the other boards we crawl, so the threshold
 * is applied board-neutrally as a heuristic rather than re-derived per board —
 * and age is a probabilistic "more likely stale" signal either way, never proof
 * the role is closed or filled. The user-facing copy below says nothing
 * board-specific, deliberately.
 */

export const STALE_AFTER_DAYS = 14;

const DAY_MS = 86_400_000;

export const STALE_TOOLTIP =
  'Older ads are more likely to have closed — but the role may still be open, ' +
  'and the contact is worth reaching out to.';

const TOOLTIP_FACT =
  'Job boards pause or bury many posts after about two weeks, regardless of ' +
  'whether the role was filled.';

export interface PostingAge {
  /** Whole days since posting, clamped to >= 0. */
  days: number;
  /** True once the ad reaches the staleness threshold. */
  isStale: boolean;
  /** Human relative age: "today", "yesterday", "6 days ago", "3 weeks ago". */
  relative: string;
  /** Human absolute date: "Jun 1, 2026". */
  absolute: string;
  /** Supporting detail for a tooltip on stale ads. */
  tooltipFact: string;
  /** False when the input date is missing or unparseable. */
  valid: boolean;
}

const INVALID: PostingAge = {
  days: 0,
  isStale: false,
  relative: '',
  absolute: '',
  tooltipFact: '',
  valid: false,
};

function formatRelative(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < STALE_AFTER_DAYS) return `${days} days ago`;
  const weeks = Math.round(days / 7);
  return `${weeks} weeks ago`;
}

export function getPostingAge(
  iso: string | null | undefined,
  now: Date = new Date(),
): PostingAge {
  if (!iso) return INVALID;

  const posted = new Date(iso);
  if (Number.isNaN(posted.getTime())) return INVALID;

  const days = Math.max(0, Math.floor((now.getTime() - posted.getTime()) / DAY_MS));

  return {
    days,
    isStale: days >= STALE_AFTER_DAYS,
    relative: formatRelative(days),
    absolute: posted.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    tooltipFact: TOOLTIP_FACT,
    valid: true,
  };
}
